import { useState, useEffect } from "react";
import { usePageTracking } from "@/hooks/use-track";
import { HelpTip } from "@/components/help-tip";
import { usePageTour } from "@/components/ProductTour";
import { TutorialTip } from "@/components/TutorialTip";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { NewQuoteModal, useNewQuoteModal } from "@/components/new-quote-modal";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/status-badge";
import { EmptyState } from "@/components/empty-state";
import { TableSkeleton, FormSkeleton } from "@/components/loading-skeleton";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import {
  FileText, Plus, Search, Clock, Filter, Sparkles, X, Pencil, Copy,
  Inbox, CheckCircle2, User, MapPin, Building2, FlaskConical, Globe2, Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Quote } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { formatMoney } from "@/lib/utils";
import { getTaxLabel } from "@shared/schema";

interface QuoteWithClient extends Quote {
  clientName?: string;
}

interface SharedQuote extends Quote {
  sharedByName: string | null;
  sharedByCompany: string | null;
  shareMessage: string | null;
  shareId: string;
  shareCreatedAt: string | null;
}

interface DraftInfo {
  itemCount: number;
  address: string;
  subtotal: number;
  savedAt: number;
}

function getDraftInfo(): DraftInfo | null {
  try {
    const raw = sessionStorage.getItem("tpt_draft_quote");
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (Date.now() - draft.savedAt > 4 * 60 * 60 * 1000) {
      sessionStorage.removeItem("tpt_draft_quote");
      return null;
    }
    if (!draft.lineItems || draft.lineItems.length === 0) return null;
    const subtotal = draft.lineItems.reduce((sum: number, item: any) => sum + (item.quantity * item.unitCost), 0);
    return {
      itemCount: draft.lineItems.length,
      address: draft.formValues?.projectAddress || "",
      subtotal,
      savedAt: draft.savedAt,
    };
  } catch {
    return null;
  }
}

export default function QuotesPage() {
  usePageTracking("Quotes");
  usePageTour("quotes");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { open: quoteModalOpen, openModal: openQuoteModal, closeModal: closeQuoteModal } = useNewQuoteModal();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [draftInfo, setDraftInfo] = useState<DraftInfo | null>(null);
  const [activeTab, setActiveTab] = useState("my-quotes");
  const [confirmDeleteQuote, setConfirmDeleteQuote] = useState<QuoteWithClient | null>(null);

  const deleteQuoteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/quotes/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({ title: "Quote deleted" });
      setConfirmDeleteQuote(null);
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to delete quote", variant: "destructive" });
    },
  });

  const duplicateQuoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/quotes/${id}/duplicate`, {});
      return response.json() as Promise<{ id: string }>;
    },
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({ title: "Quote duplicated", description: "The original is unchanged. You can now edit this copy." });
      navigate(`/quotes/${copy.id}`);
    },
    onError: (error: Error) => {
      toast({ title: "Could not duplicate quote", description: error.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    setDraftInfo(getDraftInfo());
  }, []);

  const { data: quotes, isLoading } = useQuery<QuoteWithClient[]>({
    queryKey: ["/api/quotes"],
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 5000,
  });

  const { data: shares = [], isLoading: sharesLoading } = useQuery<SharedQuote[]>({
    queryKey: ["/api/shares/incoming"],
  });

  const isAdmin = user?.email === "contact.constructivo@gmail.com";

  const { data: testQuotes = [], isLoading: testQuotesLoading } = useQuery<QuoteWithClient[]>({
    queryKey: ["/api/quotes", { onlyTests: 1 }],
    queryFn: async () => {
      const res = await fetch("/api/quotes?onlyTests=1", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch test quotes");
      return res.json();
    },
    enabled: isAdmin,
  });

  function parseTestMeta(quote: QuoteWithClient): { market: string; marketKey: string; mode: string } {
    const m = quote.quoteNumber?.match(/^TEST-([A-Z]+)-([A-Z]+)/);
    const marketCode = m?.[1] ?? "";
    const modeCode = m?.[2] ?? quote.quoteType?.toUpperCase() ?? "";
    const marketLabel: Record<string, string> = { UK: "United Kingdom", US: "United States", CA: "Canada", AU: "Australia" };
    const modeLabel: Record<string, string> = { AUTO: "Auto Quote", TEMPLATE: "Template Quote", MANUAL: "Manual Quote" };
    return {
      market: marketLabel[marketCode] ?? (user?.market ? user.market.toUpperCase() : "—"),
      marketKey: marketCode.toLowerCase() || user?.market || "uk",
      mode: modeLabel[modeCode] ?? (quote.quoteType ? quote.quoteType[0].toUpperCase() + quote.quoteType.slice(1) + " Quote" : "Quote"),
    };
  }

  const acceptMutation = useMutation({
    mutationFn: (quoteId: string) => apiRequest("POST", `/api/quotes/${quoteId}/accept-shared`, {}),
    onSuccess: (_data, quoteId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/shares/incoming"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shares/pending-count"] });
      toast({ title: "Quote accepted", description: "The quote has been added to your account." });
      navigate(`/quotes/${quoteId}`);
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to accept quote", variant: "destructive" });
    },
  });

  const declineMutation = useMutation({
    mutationFn: (quoteId: string) => apiRequest("POST", `/api/quotes/${quoteId}/decline-shared`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shares/incoming"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shares/pending-count"] });
      toast({ title: "Quote declined", description: "The shared quote has been removed." });
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to decline quote", variant: "destructive" });
    },
  });

  const filteredQuotes = quotes?.filter((quote) => {
    const matchesSearch =
      quote.quoteNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.projectAddress?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Use the shared `formatMoney` helper so the list shows the right currency
  // symbol per market: £ (UK), A$ (AU), C$ (CA). The list view used a
  // 0-decimal compact format; preserve that by trimming the ".XX" suffix that
  // `formatMoney` adds (it always returns 2dp).
  const formatCurrency = (value: number | string | null) =>
    formatMoney(Number(value || 0), user?.market).replace(/\.\d{2}$/, "");

  const formatCurrencyFull = (value: number | string | null) =>
    formatMoney(Number(value || 0), user?.market);

  // Market-aware "inc. VAT / inc. GST / inc. Sales Tax" caption used under the
  // header total on the shared-quotes tab.
  const taxLabel = getTaxLabel(user?.market);
  const inclusiveTaxCaption = `inc. ${taxLabel}`;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-quotes-title">
            Quotes
            <HelpTip testId="button-help-quotes" content="View, search, and manage all your quotes here. Use the filters to find quotes by status. Click any quote to see the full breakdown, edit it, or download the PDF." />
          </h1>
          <p className="text-muted-foreground">Manage all your project quotes</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={openQuoteModal} data-testid="button-new-quote">
            <Plus className="h-4 w-4 mr-2" />
            New Quote
          </Button>
        </div>
      </div>

      <TutorialTip id="quotes-intro" title="Managing Your Quotes">
        Every quote you create is saved here. Open any quote to edit it, share it with your client for digital sign-off, or export it. The status filter lets you track where each quote sits in your workflow. The <strong>Shared Quotes</strong> tab shows quotes sent by others for your review.
      </TutorialTip>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="tabs-quotes">
          <TabsTrigger value="my-quotes" data-testid="tab-my-quotes">
            My Quotes
          </TabsTrigger>
          <TabsTrigger value="shared-quotes" data-testid="tab-shared-quotes" className="gap-2">
            Shared Quotes
            {shares.length > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                {shares.length}
              </span>
            )}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="test-quotes" data-testid="tab-test-quotes" className="gap-2">
              <FlaskConical className="h-3.5 w-3.5" />
              Test Quotes
              {testQuotes.length > 0 && (
                <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {testQuotes.length}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* My Quotes tab */}
        <TabsContent value="my-quotes" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search quotes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-quotes"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="select-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="sent">Submitted</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="shared">Shared</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {draftInfo && (
            <Card className="border-primary/30 bg-primary/5" data-testid="card-draft-quote">
              <CardContent className="p-0">
                <div className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">Quote in progress</span>
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
                        Unsaved Draft
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {draftInfo.itemCount} line items
                      {draftInfo.address && ` \u2022 ${draftInfo.address}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="font-semibold text-lg">{formatCurrency(draftInfo.subtotal)}</div>
                      <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                        <Clock className="h-3 w-3" />
                        {(() => {
                          const mins = Math.round((Date.now() - draftInfo.savedAt) / 60000);
                          return mins < 1 ? "just now" : mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
                        })()}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate("/quotes/new?resume=1")}
                        data-testid="button-resume-draft"
                      >
                        Resume
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          sessionStorage.removeItem("tpt_draft_quote");
                          setDraftInfo(null);
                        }}
                        data-testid="button-discard-draft"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-6">
                  <TableSkeleton rows={5} />
                </div>
              ) : !filteredQuotes || filteredQuotes.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No quotes found"
                  description={searchQuery || statusFilter !== "all"
                    ? "Try adjusting your search or filters"
                    : "Create your first quote to get started"}
                  actionLabel="Create Quote"
                  onAction={openQuoteModal}
                />
              ) : (
                <div className="divide-y">
                  {filteredQuotes.map((quote) => (
                    <div
                      key={quote.id}
                      className="flex items-center gap-4 p-4 hover-elevate cursor-pointer group"
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                      data-testid={`row-quote-${quote.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{quote.quoteNumber}</span>
                          <StatusBadge status={quote.status || "draft"} />
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {quote.clientName || "No client"}
                          {quote.projectAddress && ` • ${quote.projectAddress}`}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {quote.projectType?.replace(/_/g, " ") || "Unspecified project type"}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {quote.status !== "shared" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100 transition-opacity h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateQuoteMutation.mutate(quote.id);
                              }}
                              disabled={duplicateQuoteMutation.isPending}
                              data-testid={`button-duplicate-quote-${quote.id}`}
                              aria-label={`Duplicate quote ${quote.quoteNumber}`}
                            >
                              <Copy className="h-3.5 w-3.5 md:mr-1.5" />
                              <span className="hidden md:inline">Duplicate</span>
                            </Button>
                          )}
                          {quote.status !== "shared" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/quotes/${quote.id}/edit`);
                            }}
                            data-testid={`button-edit-quote-${quote.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 md:mr-1.5" />
                            <span className="hidden md:inline">Edit</span>
                          </Button>
                        )}
                        <div className="text-right">
                          <div className="font-semibold text-lg">{formatCurrency(quote.grandTotal || 0)}</div>
                          <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                            <Clock className="h-3 w-3" />
                            {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString("en-GB") : "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shared Quotes tab */}
        <TabsContent value="shared-quotes" className="space-y-4 mt-4">
          {sharesLoading ? (
            <FormSkeleton />
          ) : shares.length === 0 ? (
            <Card>
              <CardContent className="py-16 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Inbox className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="font-medium">No shared quotes</p>
                <p className="text-sm text-muted-foreground max-w-xs">
                  When another builder shares a quote with your account, it will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {shares.map((share) => (
                <Card key={share.id} data-testid={`card-shared-quote-${share.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <CardTitle className="text-base">{share.quoteNumber}</CardTitle>
                        <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
                          <User className="h-3.5 w-3.5 shrink-0" />
                          <span>
                            {share.sharedByCompany || share.sharedByName || "Another builder"}
                            {share.sharedByCompany && share.sharedByName && ` (${share.sharedByName})`}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-semibold">{formatCurrencyFull(share.grandTotal)}</p>
                        <p className="text-xs text-muted-foreground">{inclusiveTaxCaption}</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {share.projectAddress && (
                        <div className="flex items-start gap-1.5 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span className="truncate">{share.projectAddress}</span>
                        </div>
                      )}
                      {share.projectType && (
                        <div className="flex items-start gap-1.5 text-muted-foreground">
                          <Building2 className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span className="capitalize">{share.projectType}</span>
                        </div>
                      )}
                      {share.shareCreatedAt && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span>
                            Received {new Date(share.shareCreatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                      )}
                      {(share as any).lineItems?.length !== undefined && (
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span>{(share as any).lineItems?.length ?? "—"} line items</span>
                        </div>
                      )}
                    </div>

                    {share.shareMessage && (
                      <div className="bg-muted/50 rounded-lg p-3 text-sm italic text-muted-foreground">
                        "{share.shareMessage}"
                      </div>
                    )}

                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        onClick={() => acceptMutation.mutate(share.id)}
                        disabled={acceptMutation.isPending || declineMutation.isPending}
                        data-testid={`button-accept-${share.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />
                        {acceptMutation.isPending ? "Accepting..." : "Accept Quote"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => declineMutation.mutate(share.id)}
                        disabled={acceptMutation.isPending || declineMutation.isPending}
                        className="text-destructive border-destructive/40 hover:bg-destructive/10"
                        data-testid={`button-decline-${share.id}`}
                      >
                        <X className="h-4 w-4 mr-1.5" />
                        {declineMutation.isPending ? "Declining..." : "Decline"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/quotes/${share.id}`)}
                        data-testid={`button-view-${share.id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Test Quotes tab (admin only) */}
        {isAdmin && (
          <TabsContent value="test-quotes" className="space-y-4 mt-4">
            <Card className="border-amber-300/50 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-700/40">
              <CardContent className="p-4 flex items-start gap-3 text-sm">
                <FlaskConical className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900 dark:text-amber-200">
                    Test quotes generated for review
                  </div>
                  <div className="text-amber-800/80 dark:text-amber-200/80 mt-0.5">
                    These quotes are flagged as test data and are hidden from your normal Quotes list, exports and counts. They will not be sent to clients. Use them to inspect each quoting mode end to end.
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                {testQuotesLoading ? (
                  <div className="p-6">
                    <TableSkeleton rows={6} />
                  </div>
                ) : testQuotes.length === 0 ? (
                  <EmptyState
                    icon={FlaskConical}
                    title="No test quotes yet"
                    description="Test quotes generated for review purposes will appear here."
                  />
                ) : (
                  <div className="divide-y">
                    {testQuotes.map((quote) => {
                      const meta = parseTestMeta(quote);
                      return (
                        <div
                          key={quote.id}
                          className="flex items-center gap-4 p-4 hover-elevate cursor-pointer group"
                          onClick={() => navigate(`/quotes/${quote.id}`)}
                          data-testid={`row-test-quote-${quote.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                            <FlaskConical className="h-5 w-5 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{quote.quoteNumber}</span>
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white">
                                Test
                              </span>
                              <StatusBadge status={quote.status || "draft"} />
                              <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                                <Globe2 className="h-3 w-3" />
                                {meta.market}
                              </span>
                              <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                                {meta.mode}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                              {quote.projectAddress || "—"}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {quote.projectType?.replace(/_/g, " ") || "Unspecified project type"}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/quotes/${quote.id}/edit`);
                              }}
                              data-testid={`button-edit-test-quote-${quote.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5 md:mr-1.5" />
                              <span className="hidden md:inline">Edit</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteQuote(quote);
                              }}
                              data-testid={`button-delete-test-quote-${quote.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                            <div className="text-right">
                              <div className="font-semibold text-lg">
                                {formatMoney(Number(quote.grandTotal || 0), meta.marketKey).replace(/\.\d{2}$/, "")}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                                <Clock className="h-3 w-3" />
                                {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString("en-GB") : "N/A"}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <NewQuoteModal open={quoteModalOpen} onClose={closeQuoteModal} />

      <AlertDialog open={!!confirmDeleteQuote} onOpenChange={(open) => !open && setConfirmDeleteQuote(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quote?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDeleteQuote ? (
                <>
                  This permanently removes <span className="font-medium">{confirmDeleteQuote.quoteNumber}</span> and all of its line items. This action cannot be undone.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-quote">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDeleteQuote && deleteQuoteMutation.mutate(confirmDeleteQuote.id)}
              disabled={deleteQuoteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-quote"
            >
              {deleteQuoteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
